import {
  ETextParamType_CheckRestriction,
  ETextParamType_ReplaceFilteredAvatar,
  ETextParamType_ReplaceMyAvatarCustom,
  ETextParamType_SetAvatarType,
  ETextParamType_Supported_TextureCompression_ASTC,
  ETextParamType_Supported_TextureCompression_ETC2
} from '../../logics/const';
import {hel_setText, requestSendingReceivedPacketsFromSkyway} from '../../logics/moduleCaller';
import {GetIHeliNetwork} from '../../singleton_registry';

import {CAvatarInfoApiNotification} from './CAvatarInfoApiNotification';

import type {AvatarInfo, AvatarInfoDocument, BaseAvatarInfo} from '../../types/network';
import type {PositionChannelProviderEventMap} from '../positionChannelProviders/IHeliPositionChannelProvider';

export class CAvatarInfo {
  /**
   * 座標チャンネルにおいて、自分の興味範囲へ他プレイヤーが入ったとき (remotejoin) に利用する、MyAvatar情報のリスト。
   */
  static readonly #channelSessionCodeAvatarInfoPairs: Map<string, BaseAvatarInfo> = new Map();
  /**
   * 現在自分が使用しているMyAvatar。`null` ならプリセットアバター使用中。
   */
  static #m_currentAvatarInfo: AvatarInfo | null;

  m_AvatarInfoApiNotification: CAvatarInfoApiNotification | null = null;

  /**
   * 座標チャンネル入室時、先に入室していたプレイヤーのMyAvatarを読み込むメソッド。
   *
   * {@link PositionChannelProviderEventMap}のremotejoinイベントのタイミングで呼ばれる。
   */
  static setAvatarInfoForExistingUsers(playerId: string): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    const channelSessionCode = heliNetwork.getChannelSessionCodeByPlayerId(playerId);
    if (!channelSessionCode) {
      throw new Error(
        `CHeliNetwork.getChannelSessionCodeByPlayerId('${playerId}') returns undefined.`
      );
    }
    const avatarInfo =
      // 興味範囲へ入ったプレイヤーの最新のMyAvatar情報を持っている場合
      this.#channelSessionCodeAvatarInfoPairs.get(channelSessionCode) ??
      // 興味範囲へ入ったプレイヤーが、自分が座標チャンネル入室段階ですでに興味範囲外に入室済みだったか自分より後に入室していた場合、もしくはプリセットアバターだった場合
      this.#getMyAvatarUrlByCurrentPosChannel(channelSessionCode);
    if (!avatarInfo) {
      // プリセットアバターの場合
      return;
    }
    this.#setOrUnsetAvatarInfo(channelSessionCode, avatarInfo);
  }

  /**
   * 座標チャンネルにおいて、自分の興味範囲へ他プレイヤーが入ったとき (remotejoin) に利用する、MyAvatar情報のリストを空にする。
   */
  static clearChannelSessionCodeAvatarInfoPairs(): void {
    CAvatarInfo.#channelSessionCodeAvatarInfoPairs.clear();
  }

  static async setOrUnsetCurrentAvatarInfo(avatarInfo: AvatarInfo | null): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    const playerId = heliNetwork.getPlayerId();
    if (!playerId) {
      console.error('Failed to retrieve the player ID from IHeliNetwork.');
      return;
    }

    this.#m_currentAvatarInfo = avatarInfo;

    if (avatarInfo) {
      // プリセットアバターを無効に
      // NOTE: いったん「-1」に変えておかなければ、マイアバターから同じプリセットアバターへ戻したとき、他プレイヤー視点でマイアバターのままになる
      // https://github.com/DevHIKKY/crp_client/blob/c6854ba64d6aae3453cc79e06b14274640108ae7/scripts/classes/crpFront.ts#L774-L779
      heliNetwork.sendData('avatar:-1');

      hel_setText(ETextParamType_SetAvatarType, `${playerId},${avatarInfo.avatar_type}`);

      // マイアバターがフィルターに引っかかっているかどうか
      const isFiltered = !(
        hel_setText(
          ETextParamType_CheckRestriction,
          `${avatarInfo.target_age},${avatarInfo.polygon_count}`
        ) === 1
      );

      const url = this.#defineModelURL(
        avatarInfo.hrm_etc2,
        avatarInfo.hrm_astc,
        avatarInfo.hrm_png
      );
      this.#applyMyAvatar(url, isFiltered);

      // ChannelSessionAPI経由にトークンUUIDを更新
      const returnValue = await globalThis.heliport.v3.api.channelSessionApi.setMyAvatar(
        avatarInfo.token
      );
      if (returnValue.status !== 'ok') {
        console.error(returnValue);
      }
    } else {
      const returnValue = await globalThis.heliport.v3.api.channelSessionApi.unsetMyAvatar();
      if (returnValue.status !== 'ok') {
        console.error(returnValue);
      }
    }
  }

  static #applyMyAvatar(url: string, isFiltered: boolean): void {
    // NOTE: `ETextParamType_ReplaceMyAvatarCustom` の3つ目のパラメータは、Cookieの設定を表し、`1` なら設定する、`0` なら設定しない。
    // NOTE: `ETextParamType_ReplaceMyAvatarCustom` の5つ目のパラメータは、マイアバターがフィルターに引っかかっているかどうかを表し、`1` なら設定する、`0` なら設定しない。
    hel_setText(
      ETextParamType_ReplaceMyAvatarCustom,
      `${this.#m_currentAvatarInfo?.uuid},${url},1,${this.#m_currentAvatarInfo?.polygon_count.toString()},${isFiltered ? '1' : '0'}`
    );
  }

  /**
   * 自身のアバターをダミーアバターにする。
   */
  static #applyDummyAvatar(uuid: string): void {
    // NOTE: `ETextParamType_ReplaceFilteredAvatar` の最後のパラメータは、Cookieの設定を表し、`1` なら設定する、`0` なら設定しない。
    hel_setText(ETextParamType_ReplaceFilteredAvatar, `${uuid},1`);
  }

  static getCurrentAvatarInfo(): AvatarInfo | null {
    return this.#m_currentAvatarInfo;
  }

  /**
   * チャネルセッションコードをキーとしてMyAvatarInfoを取得する
   * @param channelSessionCode
   */
  static #getMyAvatarUrlByCurrentPosChannel(channelSessionCode: string): AvatarInfo | null {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    const currentPositionChannel = heliNetwork.getCurrentPositionChannel();
    if (!currentPositionChannel) {
      console.error(
        `[ #getMyAvatarUrlByCurrentPosChannel ] noop since currentPositionChannel === null @ channelSessionCode = ${channelSessionCode}`
      );
      return null;
    }
    const positionChannelDetail = currentPositionChannel.getPositionChannelDetail();
    if (!positionChannelDetail) {
      console.error(
        `[ #getMyAvatarUrlByCurrentPosChannel ] noop since positionChannelDetail === null @ channelSessionCode = ${channelSessionCode}`
      );
      return null;
    }

    const found = positionChannelDetail.channel_sessions.find(
      (channelSession) => channelSession.channel_session_code === channelSessionCode
    );
    if (!found) {
      // 興味範囲へ入ったプレイヤーが、プリセットアバター、かつ自分が座標チャンネル入室段階ですでに興味範囲外に入室済みだったか自分より後に入室していた場合
      return null;
    }
    return found.avatar_info ?? null;
  }

  static #defineModelURL(hrmETC2: string | null, hrmASTC: string | null, hrmPNG: string): string {
    if (hrmETC2 && hel_setText(ETextParamType_Supported_TextureCompression_ETC2, '') === 1) {
      return hrmETC2;
    } else if (hrmASTC && hel_setText(ETextParamType_Supported_TextureCompression_ASTC, '') === 1) {
      return hrmASTC;
    } else {
      // ETC2もASTCも条件に合致しない場合はPNGを使用
      return hrmPNG;
    }
  }

  static #receiveDummyAvatar(playerId: string, uuid: string): void {
    requestSendingReceivedPacketsFromSkyway('data', playerId, `filteredavatar:${uuid}`);
  }

  static #setOrUnsetAvatarInfo(channelSessionCode: string, avatarInfo: BaseAvatarInfo | null) {
    // AvatarInfo一覧を更新
    if (avatarInfo) {
      CAvatarInfo.#channelSessionCodeAvatarInfoPairs.set(channelSessionCode, avatarInfo);
    } else {
      CAvatarInfo.#channelSessionCodeAvatarInfoPairs.delete(channelSessionCode);
    }

    const playerId = GetIHeliNetwork()?.getPlayerIdByChannelSessionCode(channelSessionCode);
    if (!playerId) {
      // Carnelian/CRPにおける興味範囲外で変更が行われた場合
      return;
    }

    if (avatarInfo) {
      // アバター情報の更新

      // マイアバターがフィルターに引っかかっているかどうか
      const isFiltered = !(
        hel_setText(
          ETextParamType_CheckRestriction,
          `${avatarInfo.target_age},${avatarInfo.polygon_count}`
        ) === 1
      );

      // 他プレイヤーのマイアバターロード
      const myAvatarURL = CAvatarInfo.#defineModelURL(
        avatarInfo.hrm_etc2,
        avatarInfo.hrm_astc,
        avatarInfo.hrm_png
      );
      requestSendingReceivedPacketsFromSkyway(
        'data',
        playerId,
        `myavatar:${avatarInfo.uuid}:${avatarInfo.polygon_count.toString()}:${isFiltered ? '1' : '0'}:${myAvatarURL}`
      );
    } else {
      // アバター情報の削除

      requestSendingReceivedPacketsFromSkyway('data', playerId, 'myavatar::');
    }
  }

  // コンストラクタ
  constructor() {
    this.m_AvatarInfoApiNotification = new CAvatarInfoApiNotification();
  }

  /**
   * アバター変更通知の受信処理の起動。
   */
  addReceivedAvatarInfoEventListener(fn: (avatarInfoDocument: AvatarInfoDocument) => void) {
    this.m_AvatarInfoApiNotification?.addPosChannelEvent(fn);
  }

  /**
   * アバター変更通知の受信。
   */
  receive(avatarInfoDocument: AvatarInfoDocument): void {
    const avatarInfo =
      'AvatarInfo' in avatarInfoDocument
        ? {
            uuid: avatarInfoDocument.AvatarInfo.UUID,
            target_age: avatarInfoDocument.AvatarInfo.TargetAge,
            polygon_count: avatarInfoDocument.AvatarInfo.PolygonCount,
            hrm_png: avatarInfoDocument.AvatarInfo.HrmPNG,
            hrm_astc: avatarInfoDocument.AvatarInfo.HrmASTC,
            hrm_etc2: avatarInfoDocument.AvatarInfo.HrmETC2
          }
        : null;

    CAvatarInfo.#setOrUnsetAvatarInfo(avatarInfoDocument.ChannelSessionCode, avatarInfo);
  }
}
